import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import ChatBubble from './ChatBubble';
import StickyButton from './StickyButton';
import StickyBanner from './StickyBanner';
import PopUp from './PopUp';
import AccordionHeader from 'components/Accordion/ToggleHeader';
import TextInput from 'components/form/TextInput';
import Button from 'components/Button';
import { snack } from 'utils/toast';
import { getConnectPageUrl } from '../../utils';
import { useHistory } from 'react-router';

const WidgetOptions = ({
  id,
  pathname,
  data,
  onRadioChange,
  onInputChange,
}) => {
  const [t] = useTranslation('groups');
  const history = useHistory();

  const OPTIONS = [
    {
      title: t('cc-widgets-bubble', 'Add a Floating Button'),
      key: `/connect/widgets/${id}/widgets/chat`,
      switchProps: {
        checked: data.collection_settings.chat.enabled,
        onChange: onRadioChange,
        name: 'collection_settings.chat.enabled',
      },
      render: () => (
        <ChatBubble
          id={id}
          chatOption={data.collection_settings.chat}
          onInputChange={onInputChange}
          onRadioChange={onRadioChange}
        />
      ),
    },
    {
      title: t('cc-widgets-stickyButton', 'Add a Sticky Button'),
      key: `/connect/widgets/${id}/widgets/sticky-button`,
      switchProps: {
        checked: data.collection_settings.stickyButton?.enabled,
        onChange: onRadioChange,
        name: 'collection_settings.stickyButton.enabled',
      },
      render: () => (
        <StickyButton
          id={id}
          options={data.collection_settings.stickyButton}
          onRadioChange={onRadioChange}
          onInputChange={onInputChange}
        />
      ),
    },
    {
      title: t('cc-widgets-banner', 'Sticky Banner'),
      key: `/connect/widgets/${id}/widgets/banner`,
      switchProps: {
        checked: data.collection_settings.banner.enabled,
        onChange: onRadioChange,
        name: 'collection_settings.banner.enabled',
      },
      render: () => (
        <StickyBanner
          id={id}
          banner={data.collection_settings.banner}
          onRadioChange={onRadioChange}
          onInputChange={onInputChange}
        />
      ),
    },
    {
      title: t('cc-widgets-popup', 'Timed Popup'),
      key: `/connect/widgets/${id}/widgets/popup`,
      switchProps: {
        checked: data.collection_settings.popup.enabled,
        onChange: onRadioChange,
        name: 'collection_settings.popup.enabled',
      },
      render: () => (
        <PopUp
          id={id}
          popup={data.collection_settings.popup}
          onRadioChange={onRadioChange}
          onInputChange={onInputChange}
        />
      ),
    },
    {
      title: t('cc-widgets-button', 'Button Click'),
      key: `/connect/widgets/${id}/widgets/button`,
      render: () => (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t(
              'scc-form-btn-preview-desc',
              'You can create a custom link to trigger the smart connect card overlay by updating your button link to be #ticpopup.',
            )}
          </Typography>
          <div>
            <TextInput
              fullWidth
              readOnly
              value="#ticpopup"
              endAdornment={
                <CopyToClipboard
                  text="#ticpopup"
                  onCopy={() => snack.success('Code copied!')}
                >
                  <Button edge="end" color="primary" variant="text">
                    Copy
                  </Button>
                </CopyToClipboard>
              }
            />
          </div>
        </Box>
      ),
    },
    {
      dataField: 'hash',
      title: t('cc-widgets-landing-page', 'Landing Page'),
      key: `/connect/widgets/${id}/widgets/landing-page`,
      render: () => (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Use the link below to view your Widget in full screen.
          </Typography>
          <div>
            <TextInput
              fullWidth
              readOnly
              value={getConnectPageUrl(data)}
              endAdornment={
                <CopyToClipboard
                  text={getConnectPageUrl(data)}
                  onCopy={() => snack.success('Code copied!')}
                >
                  <Button edge="end" color="primary" variant="contained">
                    Copy
                  </Button>
                </CopyToClipboard>
              }
            />
          </div>
        </Box>
      ),
    },
  ];

  return (
    <div>
      <Box mb={3}>
        {OPTIONS.map((i) => {
          const expanded = pathname === i.key;
          return (
            <Accordion
              disableGutters
              expanded={expanded}
              key={i.key}
              sx={{
                'border': 1,
                'borderColor': 'divider',
                'borderRadius': 1,
                'mb': 1,
                '&.Mui-expanded': {
                  borderColor: 'primary.main',
                },
                '&:before': {
                  display: 'none',
                },
              }}
            >
              <AccordionHeader
                title={i.title}
                eventKey={i.key}
                expanded={expanded}
                switchProps={i.switchProps}
                onClick={(key) => history.push(key)}
              />
              <AccordionDetails>{i.render()}</AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </div>
  );
};

export default WidgetOptions;
